import { backend } from '@/services/backend'
import {
  QuestionnaireSection,
  QuestionnairePage,
  QuestionnaireUserChoices,
  QuestionnaireChoice,
  Questionnaire16167Genre,
  QUESTIONNAIRE_GENRES,
  Questionnaire16167Book,
  BookSelection16167Choice,
} from './onboarding.types'
import { Sentry } from '@/services/sentry'
import { storage } from '@/helpers/storage'
import { Mixpanel } from '@/analytics/mixpanel'
import { QUESTIONNAIRE_BOOKS } from './onboarding.books'
import { assertNotNull } from '@/helpers/typing'

export class QuestionnaireView {
  sections: QuestionnaireSection[]

  private _currentSectionIndex = 0
  private _currentPageIndex = 0
  private _currentSubpageIndex = 0

  private _userChoices: QuestionnaireUserChoices = {}
  private _currentChoice?: QuestionnaireChoice

  private _nextOnboardingPage: () => void

  isContinueButtonVisible = true
  isLogoVisible = false

  /**
   * @param sections The pages data.
   * @param router The parent page's Vue Router instance.
   * @param userName The user's given name. This value will be used to replace all instances of '{{name}}'
   *   in the pages' `variableTitle` property.
   */
  constructor(
    sections: QuestionnaireSection[],
    nextOnboardingPage: () => void,
    userName?: string,
  ) {
    if (userName) {
      this.sections = this._replaceVariables(sections, userName)
    } else {
      this.sections = sections
    }

    this._userChoices = QuestionnaireView.loadUserChoices()

    this.loadPosition()

    if (this.currentSectionIndex === 0 && this.currentPageIndex === 0) {
      this.trackPageView()
      // Save the position for the first page.
      this.savePosition()
    }

    if (this.currentPage.hideContinueButton) {
      this.isContinueButtonVisible = false
    }

    if (this.currentPage.showLogo) {
      this.isLogoVisible = true
    }

    this._nextOnboardingPage = nextOnboardingPage
  }

  /**
   * Replace the `title` property of the given pages with the value of their `variableTitle`,
   * and replace all instaces of '{{name}}' with the given `userName`.
   *
   * @param sections The pages data.
   * @param userName The user's given name. This value will be used to replace all instances of '{{name}}'
   *   in the pages' `variableTitle` property.
   */
  private _replaceVariables(
    sections: QuestionnaireSection[],
    userName: string,
  ): QuestionnaireSection[] {
    return sections.map((section) => {
      const parsedPages = section.pages.map((page) => {
        if (!page.variableTitle) {
          return page
        }
        return {
          ...page,
          title: page.variableTitle.replace(/{{name}}/g, userName),
        }
      })

      return {
        ...section,
        pages: parsedPages,
      }
    })
  }

  get pages(): QuestionnairePage[] {
    return this.sections.flatMap((section) => section.pages)
  }

  get currentChoice(): QuestionnaireChoice | undefined {
    return this._currentChoice
  }

  get userChoices(): QuestionnaireUserChoices {
    return this._userChoices
  }

  get currentSectionIndex(): number {
    return this._currentSectionIndex
  }

  get currentPageIndex(): number {
    return this._currentPageIndex
  }

  get currentSubpageIndex(): number {
    return this._currentSubpageIndex
  }

  /**
   * Get the current page object.
   */
  get currentPage(): QuestionnairePage {
    return this.sections[this.currentSectionIndex].pages[this.currentPageIndex]
  }

  /**
   * Get the number of subpages in the current page.
   *
   * Returns `1` if it doesn't have subpages.
   */
  get currentPageLength(): number {
    return this.getPageLength(this.currentPage)
  }

  /**
   * Return `true` if the current subpage is the last subpage in the current page.
   */
  get isLastSubpage(): boolean {
    return this.currentSubpageIndex === this.currentPageLength - 1
  }

  /**
   * Get the current section object.
   */
  get currentSection(): QuestionnaireSection {
    return this.sections[this.currentSectionIndex]
  }

  /**
   * Get the number of pages in the current section, including subpages.
   */
  get currentSectionLength(): number {
    let count = 0

    const pages = this.currentSection.pages
    pages.map((page) => {
      count += this.getPageLength(page)
    })

    return count
  }

  /**
   * Get the length of the `pages` array of the current section.
   */
  get currentPagesLength(): number {
    return this.currentSection.pages.length
  }

  /**
   * Return `true` if the current section is the last section in the questionnaire.
   */
  get isLastSection(): boolean {
    return this.currentSectionIndex === this.sections.length - 1
  }

  /**
   * Return `true` if the current page is the last page in the current section.
   */
  get isSectionLastPage(): boolean {
    return this.currentPageIndex === this.currentPagesLength - 1
  }

  /**
   * Return `true` if the current page is the last page of the questionnaire.
   */
  get isLastPage(): boolean {
    return this.isLastSection && this.isSectionLastPage
  }

  /**
   * Get the progress towards the end of the current section.
   *
   * @Returns a decimal percentage value [0-1] representing the progress through the section.
   *
   * @example the 1st page in a 10 page section returns `0`
   * @example the 5th page in a 10 page section returns `0.4`
   * @example the 10th page in a 10 page section returns `1`
   */
  get currentSectionProgress(): number {
    return this.sectionPreviousPagesLength / this.currentSectionLength
  }

  /**
   * Get the number of pages visited in this section, including subpages.
   */
  get sectionPreviousPagesLength(): number {
    let count = this.currentSubpageIndex

    for (let i = 0; i < this.currentPageIndex; i++) {
      const page = this.currentSection.pages[i]
      count += this.getPageLength(page)
    }

    return count
  }

  /**
   * Get the number of subpages in the given page.
   *
   * Returns `1` if it doesn't have subpages.
   */
  getPageLength(page: QuestionnairePage): number {
    return page.content?.length || 1
  }

  /**
   * Get the page data with the given `pageID`.
   */
  getPage(pageID: string): QuestionnairePage | undefined {
    return this.pages.find((page) => page.id === pageID)
  }

  /**
   * Save the current Questionnaire position to local storage.
   */
  savePosition(): void {
    storage.setItem(
      'sf_lq_position',
      JSON.stringify({
        section: this.currentSectionIndex,
        page: this.currentPageIndex,
      }),
    )
  }

  /**
   * Update the Questionnaire position from the saved data in local sotrage.
   */
  loadPosition(): void {
    const positionString = storage.getItem('sf_lq_position')
    if (!positionString) {
      return
    }

    const position = JSON.parse(positionString)

    this._currentSectionIndex = position.section
    this._currentPageIndex = position.page
  }

  /**
   * Update `this._userChoices` from the data saved in local storage.
   */
  static loadUserChoices(): QuestionnaireUserChoices {
    const choicesString = storage.getItem('sf_lq_choices')
    if (!choicesString) {
      return {}
    }

    return JSON.parse(choicesString)
  }

  /**
   * Track the current page view and choices to Mixpanel.
   */
  trackPageView(): void {
    Mixpanel.trackOnboardingPageView(
      `questionnaire - ${this.currentPage.id}`,
      this.userChoices,
    )
  }

  /**
   * Navigate to the next page.
   *
   * Returns `false` if the current is the last
   * subpage, and `true` otherwise.
   *
   * It just returns a boolean instead of navigating
   * to the next page because on some pages we might want
   * to handle some extra logic before that.
   */
  nextSubpage(): boolean {
    if (this.isLastSubpage) {
      return false
    }

    this._currentSubpageIndex++
    return true
  }

  /**
   * Navigate to the next page.
   */
  async nextPage(): Promise<void> {
    this.saveUserChoice()

    if (this.isLastPage) {
      await QuestionnaireView.submitUserChoices(this.userChoices)
      this._nextOnboardingPage()
      return
    }

    // Reset the current subpage index.
    this._currentSubpageIndex = 0

    if (this.isSectionLastPage) {
      // Go to the first page of the next section.
      this._currentSectionIndex++
      this._currentPageIndex = 0
    } else {
      this._currentPageIndex++
    }

    this.trackPageView()
    this.savePosition()

    if (this.currentPage.hideContinueButton) {
      this.hideContinueButton()
    } else {
      this.showContinueButton()
    }

    if (this.currentPage.showLogo) {
      this.showLogo()
    } else {
      this.hideLogo()
    }
  }

  /**
   * Go to the next subpage, if possible, or
   * navigate to the next page.
   */
  async continue(): Promise<void> {
    const nextSubpageExists = this.nextSubpage()

    if (!nextSubpageExists) {
      await this.nextPage()
    }
  }

  /**
   * Set the value of `this._currentChoice`.
   */
  setCurrentChoice(newValue: QuestionnaireChoice | undefined): void {
    this._currentChoice = newValue
  }

  /**
   * Add the current page's choice to `this._userChoices`, and then
   * reset `this.currentPageChoice`.
   */
  saveUserChoice(): void {
    if (this._currentChoice === undefined) {
      return
    }

    // Don't add empty arrays to the choices object.
    if (
      Array.isArray(this._currentChoice) &&
      this._currentChoice.length === 0
    ) {
      this.setCurrentChoice(undefined)
      return
    }

    this._userChoices[this.currentPage.id] = this._currentChoice

    // Make a copy of the user choices to local storage so we can
    // restore it later if they leave the questionnaire page and
    // come back later.
    storage.setItem('sf_lq_choices', JSON.stringify(this.userChoices))

    // Save the selected book covers to local storage so we can reference
    // it later from outside of the questionnaire.
    if (this.currentPage.id === 'book-selection') {
      const currentChoice = this._currentChoice as BookSelection16167Choice
      const bookCovers = currentChoice.map((book) => book.cover)
      storage.setItem('sf_lq_books16167', JSON.stringify(bookCovers))
    }

    // Reset `this._currentChoice`.
    this.setCurrentChoice(undefined)
  }

  /**
   * Get the user choice from the given `pageID`.
   */
  getUserChoice<T extends QuestionnaireChoice>(pageID: string): T | undefined {
    return this.userChoices[pageID] as T | undefined
  }

  /**
   * Create the user's "First Reading List" Custom List from the choices
   * in the `book-selection` page.
   */
  async createReadingList16167(): Promise<void> {
    const selectedBooks =
      this.getUserChoice<BookSelection16167Choice>('book-selection')
    const bookIDs = assertNotNull(selectedBooks).map((book) => book.id)

    await backend.createCustomList('First Reading List', bookIDs)
  }

  /**
   * Send `this._userChoices` to the backend.
   */
  static async submitUserChoices(
    userChoices: QuestionnaireUserChoices,
  ): Promise<void> {
    try {
      await backend.saveQuestionnaire(userChoices)
    } catch (error: any) {
      Sentry.withScope((scope) => {
        scope.setExtra('message', 'Onboarding Questionnaire aborted on error')
        Sentry.captureException(error)
      })
    }

    // After submitting it to the backend,
    // we don't need this data in local storage anymore.
    storage.removeItem('sf_lq_choices')
    storage.removeItem('sf_lq_position')
  }

  /**
   * Hide the continue button.
   *
   * The continue button is the standard way to navigate through the questionnaire,
   * but some pages have their own logic to handle the navigation; so we can hide it
   * in those cases.
   */
  hideContinueButton(): void {
    this.isContinueButtonVisible = false
  }

  /**
   * Show the continue button.
   */
  showContinueButton(): void {
    this.isContinueButtonVisible = true
  }

  /**
   * Hide the logo at the top of the page.
   */
  hideLogo(): void {
    this.isLogoVisible = false
  }

  /**
   * Show the logo at the top of the page.
   */
  showLogo(): void {
    this.isLogoVisible = true
  }
}

/**
 * Import the given image from the onboarding images folder.
 */
export function getImage(image: string): string {
  return require(`@/assets.app/images/onboarding/${image}`)
}

/**
 * Get 15 books based on the given `selectedGenres`.
 *
 * This function returns an equal number of the most popular books
 * in each genre. If 15 is not divisible by the number of
 * selected genres, it divides the remainder among the first genres.
 * Eg. if `selectedGenres` has 2 items, this function will return
 * 8 books for the first genre, and 7 for the second.
 * If it has 6 items, this function will return 3 for the first 3,
 * and 2 for the last 3.
 */
export function getQuestionnaireBooks16167(
  selectedGenres: readonly Questionnaire16167Genre[] = QUESTIONNAIRE_GENRES,
): Questionnaire16167Book[] {
  const resultLength = 15
  const booksPerGenre = Math.floor(resultLength / selectedGenres.length)
  const remainder = resultLength % selectedGenres.length

  const booksToShow: Questionnaire16167Book[] = []

  selectedGenres.forEach((genre, index) => {
    // Get books in the given genre that don't already exist
    // in `booksToShow` (ie. don't add duplicates).
    const booksInThisGenre = QUESTIONNAIRE_BOOKS.filter((book) => {
      return (
        book.genre.includes(genre) &&
        !booksToShow.some((existingBook) => existingBook.id === book.id)
      )
    })

    // Divide the remainer among the first genres.
    // prettier-ignore
    const numberOfBooksToAdd = index < remainder
      ? booksPerGenre + 1
      : booksPerGenre

    booksToShow.push(...booksInThisGenre.slice(0, numberOfBooksToAdd))
  })

  // Sort the books by popularity and return them.
  return booksToShow.sort((a, b) => a.popularity - b.popularity)
}
